import type { MinimalCard } from '@/services/cardDatabase';
import type { ScryfallSet } from '@/types/card';

import type { BoosterTemplate, CardPool, DraftPack } from '../types';
import { MYTHIC_PROBABILITY, PACKS_PER_DRAFT } from '../types';

/**
 * Fisher-Yates shuffle (in-place).
 */
function shuffle<T>(array: T[]): T[] {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
}

/**
 * Pick N random unique cards from an array without modifying the original.
 */
function pickRandom<T>(pool: T[], count: number): T[] {
  const shuffled = shuffle([...pool]);
  return shuffled.slice(0, Math.min(count, shuffled.length));
}

/**
 * Determines if a card is a basic land based on type_line.
 */
function isBasicLand(card: MinimalCard): boolean {
  return card.type_line?.toLowerCase().includes('basic land') ?? false;
}

/**
 * Determines if a card is a token, emblem, or other non-booster card type.
 */
function isNonBoosterCard(card: MinimalCard): boolean {
  const typeLine = card.type_line?.toLowerCase() ?? '';
  return (
    typeLine.includes('token') ||
    typeLine.includes('emblem') ||
    typeLine.includes('art series') ||
    (typeLine.includes('card') === false && typeLine === '') // No type line at all
  );
}

/**
 * Filter a set's cards to only those eligible for booster packs.
 */
export function getBoosterEligibleCards(setCards: MinimalCard[]): MinimalCard[] {
  return setCards.filter((card) => card.rarity !== 'special' && card.rarity !== 'bonus' && !isBasicLand(card) && !isNonBoosterCard(card));
}

/**
 * Split cards into rarity pools.
 */
export function buildCardPool(cards: MinimalCard[]): CardPool {
  const commons: MinimalCard[] = [];
  const uncommons: MinimalCard[] = [];
  const rares: MinimalCard[] = [];
  const mythics: MinimalCard[] = [];

  for (const card of cards) {
    switch (card.rarity) {
      case 'common':
        commons.push(card);
        break;
      case 'uncommon':
        uncommons.push(card);
        break;
      case 'rare':
        rares.push(card);
        break;
      case 'mythic':
        mythics.push(card);
        break;
    }
  }

  return { commons, uncommons, rares, mythics };
}

/**
 * Determine booster template based on available cards.
 */
export function getBoosterTemplate(hasBonusSheet: boolean): BoosterTemplate {
  // Modern play booster: 14 cards
  // 6C + 3U + 1R/M + 1 wildcard + 1 bonus/extra + 2 flex commons = 14
  return {
    commons: hasBonusSheet ? 6 : 7,
    uncommons: 3,
    rareSlots: 1,
    bonusSheetSlot: hasBonusSheet,
    totalCards: 14,
  };
}

/**
 * Find bonus sheet (child) sets for a given parent set code.
 * Excludes token sets, promo sets, and other non-draftable child sets.
 */
export function findBonusSheetSets(parentSetCode: string, allSets: ScryfallSet[]): ScryfallSet[] {
  const NON_BONUS_SET_TYPES = ['token', 'promo', 'memorabilia', 'minigame'];
  return allSets.filter(
    (s) =>
      s.parent_set_code?.toLowerCase() === parentSetCode.toLowerCase() &&
      !NON_BONUS_SET_TYPES.includes(s.set_type) &&
      !s.code.startsWith('t'), // Token sets are prefixed with 't' (e.g., tstx, tneo)
  );
}

/**
 * Generate a single booster pack.
 */
export function generateBoosterPack(cardPool: CardPool, bonusSheetCards: MinimalCard[], template: BoosterTemplate): MinimalCard[] {
  const pack: MinimalCard[] = [];
  const usedIds = new Set<string>();

  function pickUniqueFrom(pool: MinimalCard[], count: number): MinimalCard[] {
    const available = pool.filter((c) => !usedIds.has(c.id));
    const picked = pickRandom(available, count);
    for (const card of picked) usedIds.add(card.id);
    return picked;
  }

  // Commons
  pack.push(...pickUniqueFrom(cardPool.commons, template.commons));

  // Uncommons
  pack.push(...pickUniqueFrom(cardPool.uncommons, template.uncommons));

  // Rare/Mythic slot
  const isMythic = Math.random() < MYTHIC_PROBABILITY;
  if (isMythic && cardPool.mythics.length > 0) {
    pack.push(...pickUniqueFrom(cardPool.mythics, 1));
  } else {
    pack.push(...pickUniqueFrom(cardPool.rares, 1));
  }

  // Wildcard slot (60% C, 30% U, 10% R/M)
  const wildcardRoll = Math.random();
  if (wildcardRoll < 0.1 && cardPool.rares.length > 0) {
    const isMythicWild = Math.random() < MYTHIC_PROBABILITY;
    if (isMythicWild && cardPool.mythics.length > 0) {
      pack.push(...pickUniqueFrom(cardPool.mythics, 1));
    } else {
      pack.push(...pickUniqueFrom(cardPool.rares, 1));
    }
  } else if (wildcardRoll < 0.4) {
    pack.push(...pickUniqueFrom(cardPool.uncommons, 1));
  } else {
    pack.push(...pickUniqueFrom(cardPool.commons, 1));
  }

  // Bonus sheet slot with rarity weighting (67% uncommon, 26% rare, 7% mythic)
  if (template.bonusSheetSlot && bonusSheetCards.length > 0) {
    const bonusPool = buildCardPool(bonusSheetCards);
    const bonusRoll = Math.random();
    let bonusPicked: MinimalCard[] = [];

    if (bonusRoll < 0.07 && bonusPool.mythics.length > 0) {
      bonusPicked = pickUniqueFrom(bonusPool.mythics, 1);
    } else if (bonusRoll < 0.33 && bonusPool.rares.length > 0) {
      bonusPicked = pickUniqueFrom(bonusPool.rares, 1);
    } else if (bonusPool.uncommons.length > 0) {
      bonusPicked = pickUniqueFrom(bonusPool.uncommons, 1);
    } else if (bonusPool.commons.length > 0) {
      bonusPicked = pickUniqueFrom(bonusPool.commons, 1);
    } else {
      // Fallback: any bonus sheet card
      const available = bonusSheetCards.filter((c) => !usedIds.has(c.id));
      if (available.length > 0) {
        bonusPicked = pickRandom(available, 1);
        for (const c of bonusPicked) usedIds.add(c.id);
      }
    }

    if (bonusPicked.length > 0) {
      pack.push(...bonusPicked);
    } else {
      pack.push(...pickUniqueFrom(cardPool.commons, 1));
    }
  } else {
    pack.push(...pickUniqueFrom(cardPool.commons, 1));
  }

  // Fill remaining slots with flex commons to reach totalCards
  const remaining = template.totalCards - pack.length;
  if (remaining > 0) {
    pack.push(...pickUniqueFrom(cardPool.commons, remaining));
  }

  return pack;
}

/**
 * Generate all packs for a full draft (3 packs per player in the pod).
 */
export function generateDraftPacks(
  setCards: MinimalCard[],
  bonusSheetCards: MinimalCard[],
  _allSets: ScryfallSet[],
  _setCode: string,
  podSize: number,
): DraftPack[][] {
  const eligibleCards = getBoosterEligibleCards(setCards);
  const cardPool = buildCardPool(eligibleCards);
  const hasBonusSheet = bonusSheetCards.length > 0;
  const template = getBoosterTemplate(hasBonusSheet);

  // Generate 3 rounds of packs (one per player per round)
  const allPacks: DraftPack[][] = [];

  for (let round = 0; round < PACKS_PER_DRAFT; round++) {
    const roundPacks: DraftPack[] = [];
    for (let player = 0; player < podSize; player++) {
      const cards = generateBoosterPack(cardPool, bonusSheetCards, template);
      roundPacks.push({
        cards: [...cards],
        originalCards: [...cards],
      });
    }
    allPacks.push(roundPacks);
  }

  return allPacks;
}
